/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from 'react';

import { useDebounce } from '@/hooks/useDebounce';
import { useSearchResult } from '@/hooks/useGetSearchResult';
import { useSearchData } from '@/hooks/useSearchContext';
import { querySuggestions } from '@/services/querySuggestion.service';
import { searchProduct } from '@/services/searchProduct.service';
import { Product } from '@/types';
import {
  SessionStorageAnalyticProps,
  setSessionData,
  SourceEvent,
  TriggerType,
} from '@/utils/analytics.util';
import { getCookieOrSessionId } from '@/utils/cart.util';
import { COUNTRY_CODE } from '@/utils/constants';

export function useSearchSuggestions(
  query: string,
  isNative = false,
  barcodeQuery?: string
) {
  const storeId = getCookieOrSessionId();

  const { requestPayload, processSearchResult } = useSearchResult();
  const { suggestionCacheValue, setSuggestionCache } = useSearchData();

  const [page, setPage] = useState(1);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isSuggestionsLoading, setIsSuggestionsLoading] = useState(false);
  const [isInitialProductsLoading, setIsInitialProductsLoading] =
    useState(false);

  const activeRequests = useRef<Set<string>>(new Set());
  const fetchedPages = useRef<Set<string>>(new Set());
  const fetchedSuggestions = useRef<Set<string>>(new Set());
  const cacheRef = useRef(suggestionCacheValue);
  const lastQueryRef = useRef('');
  const isQueryResetting = useRef(false);
  const [stableData, setStableData] = useState({
    suggestion: [] as string[],
    productSuggestion: [] as Product[],
    productSuggestionCount: 0,
    hasMore: true,
  });

  useEffect(() => {
    cacheRef.current = suggestionCacheValue;
  }, [suggestionCacheValue]);

  // Normalize queries
  const debouncedSuggestionQuery = useDebounce(query, 50);
  const debouncedProductQuery = useDebounce(query, 300);

  const normalizedSuggestionQuery = debouncedSuggestionQuery
    .trim()
    .toLocaleLowerCase();
  const normalizedProductQuery = debouncedProductQuery
    .trim()
    .toLocaleLowerCase();
  const normalizedRawQuery = query.trim().toLocaleLowerCase();

  useEffect(() => {
    if (lastQueryRef.current === normalizedSuggestionQuery) {
      return;
    }

    lastQueryRef.current = normalizedSuggestionQuery;
    isQueryResetting.current = true;
    fetchedPages.current.clear();
    fetchedSuggestions.current.clear();
    setPage(1);
  }, [normalizedSuggestionQuery]);

  const isSuggestionMatch =
    suggestionCacheValue?.query === normalizedSuggestionQuery;
  const isProductMatch = suggestionCacheValue?.query === normalizedProductQuery;

  const suggestion = stableData.suggestion;

  const productSuggestion = stableData.productSuggestion;

  const productSuggestionCount = stableData.productSuggestionCount;

  const hasMore = stableData.hasMore;

  useEffect(() => {
    if (!suggestionCacheValue?.data) return;

    const hasValidSuggestionUpdate = isSuggestionMatch;
    const hasValidProductUpdate = isProductMatch;

    if (!hasValidSuggestionUpdate && !hasValidProductUpdate) {
      return;
    }

    const setPreservedData = () => {
      setStableData((prev) => ({
        suggestion: hasValidSuggestionUpdate
          ? (suggestionCacheValue.data.suggestion ?? prev.suggestion)
          : prev.suggestion,

        productSuggestion: hasValidProductUpdate
          ? (suggestionCacheValue.data.productSuggestion ??
            prev.productSuggestion)
          : prev.productSuggestion,

        productSuggestionCount: hasValidProductUpdate
          ? (suggestionCacheValue.data.productSuggestionCount ??
            prev.productSuggestionCount)
          : prev.productSuggestionCount,

        hasMore: hasValidProductUpdate
          ? (suggestionCacheValue.data.hasMore ?? prev.hasMore)
          : prev.hasMore,
      }));
    };

    setPreservedData();
  }, [suggestionCacheValue, isSuggestionMatch, isProductMatch]);

  const stickySuggestion =
    normalizedRawQuery.length <= 1
      ? []
      : isSuggestionMatch && suggestionCacheValue?.data.suggestion
        ? suggestionCacheValue.data.suggestion
        : suggestion;

  const isSearchingSuggestion =
    (normalizedRawQuery.length > 1 &&
      normalizedRawQuery !== normalizedSuggestionQuery) ||
    (normalizedSuggestionQuery.length > 1 && !isSuggestionMatch) ||
    isSuggestionsLoading;

  const isSearchingProducts =
    (normalizedRawQuery.length > 1 &&
      normalizedRawQuery !== normalizedProductQuery) ||
    (normalizedProductQuery.length > 1 && !isProductMatch) ||
    isInitialProductsLoading;

  const isInitialSearching = isSearchingSuggestion || isSearchingProducts;

  useEffect(() => {
    if (normalizedSuggestionQuery.length <= 1) {
      return;
    }

    if (fetchedSuggestions.current.has(normalizedSuggestionQuery)) return;

    const currentCache = cacheRef.current;
    if (
      currentCache?.query === normalizedSuggestionQuery &&
      currentCache.data.suggestion !== undefined
    ) {
      fetchedSuggestions.current.add(normalizedSuggestionQuery);
      return;
    }

    const requestKey = `suggestions-${normalizedSuggestionQuery}`;
    if (activeRequests.current.has(requestKey)) return;
    activeRequests.current.add(requestKey);

    let cancelled = false;

    const fetchSuggestions = async () => {
      if (suggestion.length === 0) {
        setIsSuggestionsLoading(true);
      }

      try {
        const suggestionsResponse = await querySuggestions({
          keyword: normalizedSuggestionQuery,
        });
        const mappedSuggestions = suggestionsResponse.map((s) => s.name);
        setSessionData(
          SessionStorageAnalyticProps.SEARCH_PERFORM_ANALYTIC_PROPS,
          {
            sourceEvent: SourceEvent.MANUAL_SEARCH,
            triggerType: TriggerType.ENTER,
            querySuggestionRecordFound: mappedSuggestions,
          }
        );
        fetchedSuggestions.current.add(normalizedSuggestionQuery);

        setSuggestionCache(normalizedSuggestionQuery, {
          suggestion: mappedSuggestions,
        });
      } catch (error) {
        console.error('Error fetching suggestions:', error);
      } finally {
        activeRequests.current.delete(requestKey);
        if (!cancelled) setIsSuggestionsLoading(false);
      }
    };

    fetchSuggestions();
    return () => {
      cancelled = true;
    };
  }, [normalizedSuggestionQuery, setSuggestionCache]);

  // Products Fetch Effect
  useEffect(() => {
    if (normalizedProductQuery.length <= 1) return;

    if (isQueryResetting.current && page !== 1) return;
    isQueryResetting.current = false;

    const pageKey = `${normalizedProductQuery}-${page}`;
    if (fetchedPages.current.has(pageKey)) return;

    const requestKey = `products-${normalizedProductQuery}-${page}`;
    if (activeRequests.current.has(requestKey)) return;
    activeRequests.current.add(requestKey);

    let cancelled = false;

    const fetchProducts = async () => {
      if (page > 1) setIsLoadingMore(true);
      if (page === 1 && productSuggestion.length === 0) {
        setIsInitialProductsLoading(true);
      }

      try {
        const query = barcodeQuery ? '*' : normalizedProductQuery;
        const payload = requestPayload(query, page);
        payload.filters = {
          countryCode: COUNTRY_CODE,
          storeCodes: storeId ? [storeId.toString()] : [],
          availability: true,
          ...(barcodeQuery ? { barcode: barcodeQuery } : {}),
        };
        const result = await searchProduct(payload);
        const autocorrectedQuery = result?.request_params?.autocorrectedQuery;
        const boostedCategory = result?.request_params?.boostedCategory;
        setSessionData(SessionStorageAnalyticProps.PRODUCT_SUGGESTION_PROPS, {
          autocorrectedQuery,
          boostedCategory,
        });
        const { products, totalProducts } = await processSearchResult(result);

        const currentSnap = cacheRef.current;
        const isMatchSnap = currentSnap?.query === normalizedProductQuery;

        let updated: Product[] = [];
        if (page === 1) {
          updated = products;
        } else {
          const existingProducts = isMatchSnap
            ? currentSnap.data.productSuggestion
            : [];

          const productMap = new Map();
          existingProducts.forEach((p) => productMap.set(p.id, p));
          products.forEach((p) => productMap.set(p.id, p));

          updated = Array.from(productMap.values());
        }

        const moreAvailable = updated.length < totalProducts;

        fetchedPages.current.add(pageKey);

        setSuggestionCache(normalizedProductQuery, {
          productSuggestion: updated,
          productSuggestionCount: totalProducts,
          hasMore: moreAvailable,
        });
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        activeRequests.current.delete(requestKey);
        if (!cancelled) {
          setIsLoadingMore(false);
          setIsInitialProductsLoading(false);
        }
      }
    };

    fetchProducts();
    return () => {
      cancelled = true;
    };
  }, [
    normalizedProductQuery,
    page,
    setSuggestionCache,
    processSearchResult,
    requestPayload,
    barcodeQuery,
  ]);

  const loadMore = () => {
    if (isLoadingMore || !hasMore) return;
    setPage((prev) => prev + 1);
  };

  return {
    suggestion: stickySuggestion,
    productSuggestion,
    productSuggestionCount,
    isInitialSearching,
    isSearchingSuggestion,
    isSearchingProducts,
    loadMore,
    hasMore,
    isLoadingMore,
    isNative,
  };
}
